import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionHeader } from "./Reveal";
import { ECOSYSTEM } from "../data/site";

const Ecosystem = ({ openContact }) => {
  const [active, setActive] = useState(0);
  const item = ECOSYSTEM[active];

  return (
    <section id="ecosystem" data-testid="ecosystem-section" className="relative py-28 md:py-40 bg-navy-dark/40">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeader
          index={2}
          overline="Technology Ecosystem"
          title={<>One Ecosystem.<br /><span className="text-smipl-orange">Multiple Technologies.</span></>}
          sub="Five solution pillars, engineered to interlock — explore each layer of the SMIPL ecosystem."
        />
        <div className="grid lg:grid-cols-[320px_1fr] gap-8">
          <Reveal className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {ECOSYSTEM.map((e, i) => (
              <button
                key={e.id}
                data-testid={`ecosystem-tab-${e.id}`}
                onClick={() => setActive(i)}
                className={`group relative flex items-center gap-4 shrink-0 lg:shrink rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                  active === i
                    ? "border-smipl-orange/60 bg-smipl-orange/10"
                    : "border-white/8 bg-white/[0.03] hover:border-white/20"
                }`}
              >
                <span className={`font-mono text-[11px] ${active === i ? "text-smipl-orange" : "text-slate-600"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <e.icon size={18} className={active === i ? "text-smipl-orange" : "text-slate-500 group-hover:text-slate-300"} />
                <span className={`font-display font-bold tracking-[0.15em] text-sm ${active === i ? "text-white" : "text-slate-400"}`}>
                  {e.label}
                </span>
              </button>
            ))}
          </Reveal>

          <Reveal delay={0.1} className="relative min-h-[380px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`ecosystem-panel-${item.id}`}
                className="h-full rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-8 md:p-12"
              >
                <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange">{item.label}</p>
                <h3 className="mt-3 font-display text-2xl md:text-4xl font-bold">{item.title}</h3>
                <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed max-w-xl">{item.blurb}</p>
                <div className="mt-8 grid sm:grid-cols-2 gap-3">
                  {item.capabilities.map((c) => (
                    <div key={c} className="flex items-center gap-3 rounded-xl border border-white/8 bg-navy-deep/60 px-4 py-3">
                      <Check size={15} className="text-smipl-orange shrink-0" />
                      <span className="text-sm text-slate-300">{c}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/8 pt-6">
                  <p className="text-sm text-slate-500 italic">{item.outcome}</p>
                  <button
                    data-testid={`ecosystem-cta-${item.id}`}
                    onClick={openContact}
                    className="group flex items-center gap-2 text-sm font-semibold text-smipl-orange hover:text-smipl-amber transition-colors"
                  >
                    Discuss {item.title} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="mt-10">
          <Link to="/solutions" data-testid="ecosystem-view-all" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-smipl-orange hover:text-smipl-amber transition-colors">
            View all solution pages <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default Ecosystem;
